import i18next, { type i18n } from "i18next";
import {
	DEFAULT_LANGUAGE,
	type en,
	type Language,
	namespaces,
	resources,
	SUPPORTED_LANGUAGES,
} from "./resources";

declare module "i18next" {
	interface CustomTypeOptions {
		defaultNS: "common";
		resources: typeof en;
	}
}

export type { i18n, TFunction } from "i18next";

/** The storefront's English strings, as the storefront passes them to its browser code. */
export type StorefrontMessages = (typeof en)["storefront"];
export {
	DEFAULT_LANGUAGE,
	type Language,
	type Namespace,
	namespaces,
	SUPPORTED_LANGUAGES,
} from "./resources";

/**
 * Picks the first supported language from a list of preferences such as
 * `navigator.languages` on the web or `getLocales()` from expo-localization.
 */
export function resolveLanguage(preferred: readonly string[] | undefined): Language {
	for (const tag of preferred ?? []) {
		const base = tag.toLowerCase().split(/[-_]/)[0];
		const match = SUPPORTED_LANGUAGES.find((l) => l === base);
		if (match) return match;
	}
	return DEFAULT_LANGUAGE;
}

/**
 * Creates an initialised i18next instance. Resources are bundled, so this is
 * synchronous and the first render already has text. Works in browsers and in
 * React Native.
 */
export function createI18n(language: Language = DEFAULT_LANGUAGE): i18n {
	const instance = i18next.createInstance();
	void instance.init({
		lng: language,
		fallbackLng: DEFAULT_LANGUAGE,
		supportedLngs: [...SUPPORTED_LANGUAGES],
		resources,
		ns: namespaces,
		defaultNS: "common",
		interpolation: { escapeValue: false }, // React escapes output
		returnNull: false,
		initAsync: false,
	});
	return instance;
}
